<template>
  <div>
    <div class="py-5 sm:py-16 sm:px-5 px-2 mx-auto max-w-2xl sm:pb-0 pb-16">
      <ContactHeader />
      <ClientOnly>
        <ContactNoAuth v-if="!user" />
      </ClientOnly>

      <form
        name="Contact Sono Team"
        @submit.prevent="submitContactForm"
        class="space-y-5 border border-gray-100 bg-white sm:rounded-2xl rounded-lg sm:p-8 p-2.5"
      >
        <p class="mb-4 text-center text-gray-500 sm:text-base text-sm">
          {{ $t("contact.header_text") }}
          <a href="mailto:contact@medium.am" class="underline text-primary"
            >contact@medium.am</a
          >.
        </p>

        <div>
          <label for="selectSubject" class="mb-1 text-sm block">
            {{ $t("contact.subject") }} <span class="text-rose-500">*</span>
          </label>
          <select
            required
            :disabled="!user"
            id="selectSubject"
            class="w-full p-3 text-base border border-gray-200 rounded outline-none focus:ring-1 focus:ring-primary"
            v-model="subject"
          >
            <option disabled value="">{{ $t("contact.select_topic") }}</option>
            <option v-for="s in subjectOptions" :key="s.value" :value="s.label">
              {{ s.label }}
            </option>
          </select>
        </div>
        <BaseTextArea
          required
          :disabled="!user"
          :label="$t('contact.message')"
          :placeholder="$t('contact.placeholder')"
          v-model="message"
        />

        <BaseButton
          :loading="contactStore.contactLoading"
          :disabled="contactStore.contactLoading || !user"
          type="submit"
          class="w-full"
          :label="$t('contact.submit')"
        />
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useContactStore } from "~/stores/contact";
import type { AppUser } from "~~/types/user";
import { useI18n } from "vue-i18n";
import { useMessage } from "~/stores/app-toast";

const contactStore = useContactStore();
const authStore = useAuthStore();
const { t } = useI18n();

const message = ref("");
const subject = ref("");
const user = useState<AppUser | null>("auth-user");
const toast = useMessage();

const subjectOptions = computed(() => [
  { value: "general", label: t("contact.subjects.general") },
  { value: "verification", label: t("contact.subjects.verification") },
  { value: "bug", label: t("contact.subjects.bug") },
  { value: "copyright", label: t("contact.subjects.copyright") },
  { value: "feature", label: t("contact.subjects.feature") },
  { value: "account", label: t("contact.subjects.account") },
]);

const submitContactForm = async () => {
  if (!message.value) {
    toast.add(t("messages.message_required"), "error");
    return;
  }
  await contactStore.submitContactMessage(message.value, subject.value);
};

useSeoMeta({
  title: () => t("contact_seo_meta.title"),
  description: () => t("contact_seo_meta.description"),
  // Social
  ogTitle: () => t("contact_seo_meta.ogTitle"),
  ogDescription: () => t("contact_seo_meta.ogDescription"),
  ogImage: "https://medium.am/og/logo.png",
  ogType: "website",
  twitterCard: "summary_large_image",
});
</script>
